import type { InstallationResolveRequest, ResolvedInstallationOrder } from "../types/installation";
import type { RemovalResolveRequest, ResolvedRemovalOrder } from "../types/removal";
import type { SalesResolveRequest, ResolvedSalesOrder } from "../types/sales";

export type OrderResolveApiError = {
  message: string;
  status: number;
};

/**
 * POST /orders/resolve
 * All business fields go inside `payload` (backend module still evolving).
 */
export async function postInstallationOrderResolve(
  token: string,
  request: InstallationResolveRequest
): Promise<ResolvedInstallationOrder> {
  const res = await fetch(`${process.env.REACT_APP_API_URL}/orders/resolve`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      order_type: request.order_type,
      ...(request.action ? { action: request.action } : {}),
      payload: request.payload,
    }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const message =
      (data as { message?: string })?.message ||
      (data as { error?: string })?.error ||
      `Order resolve failed (${res.status})`;
    const err: OrderResolveApiError = { message: String(message), status: res.status };
    throw err;
  }

  // SuccessResponse wrapper: { data: ... } or direct order
  const resolved =
    (data as { data?: ResolvedInstallationOrder })?.data ??
    (data as ResolvedInstallationOrder);

  return resolved;
}

/** POST /orders/resolve for removal orders (`order_type: "removal"`). */
export async function postRemovalOrderResolve(
  token: string,
  request: RemovalResolveRequest
): Promise<ResolvedRemovalOrder> {
  const res = await fetch(`${process.env.REACT_APP_API_URL}/orders/resolve`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      order_type: request.order_type,
      ...(request.action ? { action: request.action } : {}),
      payload: request.payload,
    }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const message =
      (data as { message?: string })?.message ||
      (data as { error?: string })?.error ||
      `Removal order resolve failed (${res.status})`;
    const err: OrderResolveApiError = { message: String(message), status: res.status };
    throw err;
  }

  const resolved =
    (data as { data?: ResolvedRemovalOrder })?.data ?? (data as ResolvedRemovalOrder);

  return resolved;
}

/** POST /orders/resolve for sales orders (`order_type: "order"`). */
export async function postSalesOrderResolve(
  token: string,
  request: SalesResolveRequest
): Promise<ResolvedSalesOrder> {
  const res = await fetch(`${process.env.REACT_APP_API_URL}/orders/resolve`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      order_type: request.order_type,
      action: request.action ?? "",
      ...(request.shipping_integration_id
        ? { shipping_integration_id: request.shipping_integration_id }
        : {}),
      payload: request.payload,
    }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const message =
      (data as { message?: string })?.message ||
      (data as { error?: string })?.error ||
      `Sales order resolve failed (${res.status})`;
    const err: OrderResolveApiError = { message: String(message), status: res.status };
    throw err;
  }

  const resolved =
    (data as { data?: ResolvedSalesOrder })?.data ?? (data as ResolvedSalesOrder);

  return resolved;
}
